/**
 * CORE-1739 — показ и скачивание файла из чата сообщества (мастера).
 *
 * Сервер отдаёт файл только тому, кому выдал ссылку, и только с его токеном
 * входа (QA FAIL 19.10: пересланную свежую ссылку первым открывал посторонний —
 * и получал файл). `<img src>` и новая вкладка токена не несут, поэтому файл
 * берётся запросом с `Authorization` и показывается из памяти: картинка —
 * object URL, документ — скачивание из того же blob.
 *
 * Только веб: чаты сообщества рисуют веб-панели дашборда, в приложении их нет.
 */
import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { api } from '../api';

type Fetch = (linkPath: string, download: boolean) => Promise<Blob>;

const defaultFetch: Fetch = (linkPath, download) => api.fetchCommunityChatAttachment(linkPath, download);

/** Object URL файла или `null` — доступа нет, файл удалён, сеть. Отозвать — `URL.revokeObjectURL`. */
export async function communityAttachmentObjectUrl(
  linkPath: string,
  fetchFile: Fetch = defaultFetch,
): Promise<string | null> {
  if (Platform.OS !== 'web' || typeof URL === 'undefined' || !URL.createObjectURL) return null;
  try {
    return URL.createObjectURL(await fetchFile(linkPath, false));
  } catch {
    return null;
  }
}

/**
 * Скачать документ под его именем. `false` — файла не дали: экран пишет
 * «Файл недоступен».
 */
export async function downloadCommunityAttachment(
  linkPath: string,
  fileName: string,
  fetchFile: Fetch = defaultFetch,
): Promise<boolean> {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return false;
  let blob: Blob;
  try {
    blob = await fetchFile(linkPath, true);
  } catch {
    return false;
  }
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Браузер уже забрал blob в загрузку — адрес можно отпустить чуть позже.
  setTimeout(() => URL.revokeObjectURL(href), 30_000);
  return true;
}

/**
 * Картинка из сообщения: берётся один раз при показе и живёт, пока строка на
 * экране; тот же адрес открывает просмотрщик без нового запроса. `failed` —
 * показать нечего.
 */
export function useCommunityAttachmentImage(
  linkPath: string | null,
  fetchFile: Fetch = defaultFetch,
): { uri: string | null; failed: boolean } {
  const [state, setState] = useState<{ uri: string | null; failed: boolean }>({ uri: null, failed: !linkPath });

  useEffect(() => {
    if (!linkPath) return;
    let alive = true;
    let created: string | null = null;
    void communityAttachmentObjectUrl(linkPath, fetchFile).then((uri) => {
      if (!alive) { if (uri) URL.revokeObjectURL(uri); return; }
      created = uri;
      setState({ uri, failed: !uri });
    });
    return () => {
      alive = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [linkPath, fetchFile]);

  return state;
}
